import { Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { generatePath, Navigate, useParams } from 'react-router-dom'

import { StickyBox } from 'components/Box'
import { CommonFormContextProps, SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import { Link } from 'components/Link'
import Loading from 'components/Loading'
import Tabs, { getPage, Tab } from 'components/Tabs'
import { ConfirmationDialog, useConfirmationDialog } from 'features/ab-tests'
import { AbTest } from 'features/ab-tests/types/abTest'
import { Alert } from 'features/alerts/types/alert'
import {
  AddEditAdUnitContext,
  AddEditAdUnitContextType,
} from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useGetAdUnit } from 'features/inventory/ad-unit/forms/GeneralForm/hooks/useGetAdUnit'
import { DomainAlerts } from 'features/inventory/domain'
import { DomainAlert } from 'features/inventory/domain/types/DomainAlert'
import { generateSelector } from 'providers/product-fruits/utils'
import { ROUTES } from 'routes'
import { ProductTab } from 'types/productTab'
import { useGetFreshAdUnitDomainAlerts } from '../../forms/GeneralForm/hooks/useGetFreshAdUnitDomainAlerts'
import { EditAdUnitUrlParams } from './types'
import { useProductTabs } from './useProductTabs'

export const EditAdUnit = (): React.JSX.Element => {
  const { id: urlAdUnitId, tab: urlCurrentTab, workspaceId } = useParams<EditAdUnitUrlParams>()
  const { t } = useTranslation('features/adUnit')
  const { adUnit } = useGetAdUnit({ id: urlAdUnitId })
  const freshAdUnitDomainAlerts: Alert<DomainAlert>[] = useGetFreshAdUnitDomainAlerts({
    adUnitId: urlAdUnitId,
  })
  const tabs: Tab<ProductTab>[] = useProductTabs(adUnit)
  const { confirmationHandler, isDialogOpen, setIsDialogOpen } = useConfirmationDialog()

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: 'ad-unit-form-edit',
  })

  if (!workspaceId || !urlAdUnitId) {
    return <Navigate to={ROUTES.BASE.NOT_FOUND} />
  } else if (!adUnit) {
    return <Loading />
  }

  const { path } = adUnit

  const contextProps: AddEditAdUnitContextType = {
    adUnit,
    ...commonContextProps,
  }

  const activeAbTestId: AbTest['id'] | undefined = adUnit.domain.activeAbTest?.id

  const Actions = (ctxProps: AddEditAdUnitContextType): React.JSX.Element => (
    <>
      <Link
        to={generatePath(ROUTES.INVENTORY.DOMAIN.EDIT, {
          id: adUnit.domain.id,
          tab: urlCurrentTab,
          workspaceId,
        })}
      >
        <Button
          data-cy='edit-domain-button'
          data-product-fruits={generateSelector({ hint: 1 })}
        >
          {t('edit.pageHeader.actions.editDomain')}
        </Button>
      </Link>

      <SaveButton
        {...ctxProps}
        {...(activeAbTestId && { onConfirmation: confirmationHandler })}
      />
    </>
  )

  return (
    <AddEditAdUnitContext.Provider value={contextProps}>
      <StickyBox>
        <AddEditAdUnitContext.Consumer>
          {(ctxProps: AddEditAdUnitContextType): React.JSX.Element => (
            <>
              <PageHeader
                actions={<Actions {...ctxProps} />}
                backTo={generatePath(ROUTES.INVENTORY.AD_UNIT.LIST, { workspaceId })}
                title={path}
                titleAdornment={{ end: <DomainAlerts alerts={freshAdUnitDomainAlerts} /> }}
              />

              {activeAbTestId && (
                <ConfirmationDialog
                  activeAbTestId={activeAbTestId}
                  formProps={commonContextProps}
                  isOpen={isDialogOpen}
                  setOpen={setIsDialogOpen}
                />
              )}
            </>
          )}
        </AddEditAdUnitContext.Consumer>

        <Tabs
          path={ROUTES.INVENTORY.AD_UNIT.EDIT}
          productFruitsSelector={generateSelector({ step: 2, tour: 2 })}
          tabs={tabs}
        />
      </StickyBox>

      {getPage<ProductTab>(tabs, urlCurrentTab, 'general')}
    </AddEditAdUnitContext.Provider>
  )
}
